'use client';

import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { api } from '@/components/AuthProvider'; 
import { useLanguage } from '@/components/LanguageProvider'; 
import { useServerIp } from '@/hooks/useServerIp'; 
import { 
    Users, Copy, Check, Activity, Search, Calendar, 
    CalendarDays, Clock, Loader2, ChevronDown, Filter, X
} from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, 
    Tooltip, ResponsiveContainer 
} from 'recharts';
import Link from 'next/link';

const API_BASE = 'https://sg-public-api.royolands.com/rpweb/api';

type TabType = 'all' | 'online' | 'month' | 'week' | 'yesterday' | 'today';
type SortType = 'playtime' | 'lastSeen' | 'name';

interface Player {
  id: number;
  nickname: string;
  isOnline: boolean;
  lastSeen: string;
  totalMinutes: number;
}

export default function StatsClient() {
  const { t, locale } = useLanguage(); 
  
  // Достаем IP и настройки видимости из единого менеджера
  const { javaIp, bedrockIp, showBedrock } = useServerIp();
  
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [sortBy, setSortBy] = useState<SortType>('playtime');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [players, setPlayers] = useState<Player[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [copiedIp, setCopiedIp] = useState<string | null>(null);
  
  const [chartData, setChartData] = useState<any[]>([]);
  const [currentOnline, setCurrentOnline] = useState(0);
  const [isChartLoading, setIsChartLoading] = useState(true);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchChartData = async () => {
    try {
      setIsChartLoading(true);
      const res = await api.get(`${API_BASE}/stats/chart`);
      setChartData(res?.data?.chartData || []);
      setCurrentOnline(res?.data?.currentOnline || 0);
    } catch (e) {
      console.error('Ошибка загрузки графика:', e);
    } finally {
      setIsChartLoading(false);
    }
  };

  const fetchPlayers = async (tab: TabType, pageNum: number, search: string, sort: SortType, isNewTab = false) => {
    if (isNewTab) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const res = await api.get(`${API_BASE}/stats/players?tab=${tab}&page=${pageNum}&limit=20&search=${search}&sort=${sort}`);
      
      const fetchedUsers = res?.data?.users || [];
      
      if (isNewTab) {
          setPlayers(fetchedUsers);
      } else {
          setPlayers(prev => [...prev, ...fetchedUsers]);
      }
      
      setHasMore(res?.data?.hasMore || false);
    } catch (e) {
      console.error('Ошибка загрузки игроков:', e);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };


  useEffect(() => {
    fetchChartData();
  }, []);

  useEffect(() => {
    setPage(1);
    fetchPlayers(activeTab, 1, debouncedSearch, sortBy, true);
  }, [activeTab, debouncedSearch, sortBy]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPlayers(activeTab, nextPage, debouncedSearch, sortBy, false);
  };

  const copyIp = (ip: string) => {
    navigator.clipboard.writeText(ip);
    setCopiedIp(ip);
    setTimeout(() => setCopiedIp(null), 2000);
  };

  const tabs = [
    { id: 'all', label: t.stats.tabs.all, icon: Users },
    { id: 'online', label: t.stats.tabs.online, icon: Activity },
    { id: 'today', label: t.stats.tabs.today, icon: Clock },
    { id: 'yesterday', label: t.stats.tabs.yesterday, icon: Clock },
    { id: 'week', label: t.stats.tabs.week, icon: Calendar },
    { id: 'month', label: t.stats.tabs.month, icon: CalendarDays },
  ];

  const dateLocale = locale === 'ru' ? 'ru-RU' : 'en-US';

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 w-full pb-12">
        
        <div className="mb-8 pt-6">
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight flex items-center gap-3">
                <Activity size={32} className="text-indigo-400" />
                {t.stats.title}
            </h1>
            <p className="text-sm text-zinc-400 mt-2">
                {t.stats.subtitle}
            </p>
        </div>

        {/* График и карточка онлайна */}
        <div className="bg-[#1e1e1e] border border-white/5 rounded-[24px] p-6 shadow-lg mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white mb-3">RpLands</h2>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button 
                            onClick={() => copyIp(javaIp)}
                            className="bg-[#2a2a2a] hover:bg-[#333333] border border-white/5 text-zinc-300 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition cursor-pointer select-none"
                        >
                            {copiedIp === javaIp ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                            {copiedIp === javaIp ? t.stats.copied : `Java: ${javaIp}`}
                        </button>
                        
                        {showBedrock && (
                          <button 
                              onClick={() => copyIp(bedrockIp)}
                              className="bg-[#2a2a2a] hover:bg-[#333333] border border-white/5 text-zinc-300 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition cursor-pointer select-none"
                          >
                              {copiedIp === bedrockIp ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                              {copiedIp === bedrockIp ? t.stats.copied : `Bedrock: ${bedrockIp}`}
                          </button>
                        )}
                    </div>
                </div>
                
                <div className="bg-[#121212] border border-white/5 rounded-2xl px-8 py-4 text-center flex flex-col items-center justify-center">
                    <p className="text-sm text-zinc-400 mb-1 font-medium">{t.stats.onlineNow}</p>
                    <p className="text-4xl font-bold text-white flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]"></span>
                        {currentOnline}
                    </p>
                </div>
            </div>

            <div className="h-[300px] w-full">
                {isChartLoading ? (
                    <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-xl">
                        <Loader2 size={32} className="animate-spin text-zinc-500 mb-3" />
                        <span className="text-zinc-500 text-sm">{t.stats.gatheringStats}</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="colorPlayers" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                            <XAxis dataKey="time" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} minTickGap={30} />
                            <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#121212', borderColor: '#ffffff10', borderRadius: '12px', color: '#fff' }}
                                itemStyle={{ color: '#818cf8' }}
                                labelStyle={{ color: '#a1a1aa', marginBottom: '4px' }}
                            />
                            <Area type="monotone" dataKey="players" name={t.stats.chartPlayers} stroke="#818cf8" strokeWidth={3} fillOpacity={1} fill="url(#colorPlayers)" />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>

        {/* ПАНЕЛЬ УПРАВЛЕНИЯ */}
        <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 mb-6">
            
            {/* Вкладки */}
            <div className="w-full xl:w-auto">
                <div className="flex flex-wrap items-center gap-2 p-1 bg-[#121212] rounded-xl border border-white/5">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button 
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as TabType)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition cursor-pointer ${
                                    activeTab === tab.id 
                                        ? 'bg-[#2a2a2a] text-white shadow' 
                                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'
                                }`}
                            >
                                <Icon size={16} className={activeTab === tab.id && tab.id === 'online' ? 'text-green-400' : ''} /> 
                                {tab.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Фильтры */}
            <div className="flex flex-col sm:flex-row w-full xl:w-auto gap-3">
                <div className="relative w-full sm:w-[240px]">
                    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                    <input 
                        type="text" 
                        placeholder={t.stats.searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-[46px] bg-[#121212] border border-white/5 rounded-xl pl-10 pr-10 text-sm text-zinc-200 focus:outline-none focus:border-indigo-500/50 transition"
                    />
                    {searchQuery && (
                        <button 
                            onClick={() => setSearchQuery('')} 
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition cursor-pointer"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="relative w-full sm:w-[180px]">
                    <Filter size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortType)}
                        className="w-full h-[46px] appearance-none bg-[#121212] border border-white/5 rounded-xl pl-10 pr-10 text-sm text-zinc-200 focus:outline-none focus:border-indigo-500/50 transition cursor-pointer"
                    >
                        <option value="playtime">{t.stats.sort.playtime}</option>
                        <option value="lastSeen">{t.stats.sort.lastSeen}</option>
                        <option value="name">{t.stats.sort.name}</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                </div>
            </div>
        </div>

        {/* Список игроков */}
        {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 size={40} className="animate-spin text-indigo-500 mb-4" />
                <p className="text-zinc-500 font-medium">{t.stats.loadingPlayers}</p>
            </div>
        ) : players.length === 0 ? (
            <div className="text-center py-20 text-zinc-500 border-2 border-dashed border-white/5 rounded-[32px] bg-[#1e1e1e]/50">
                <Search size={48} className="mx-auto mb-4 opacity-20" />
                <h3 className="text-xl font-bold text-zinc-300 mb-2">{t.stats.notFoundTitle}</h3>
                <p className="text-sm">{t.stats.notFoundDesc}</p>
            </div>
        ) : (
            <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                    {players.map((player) => (
                        <Link 
                            href={`/user?nickname=${player.nickname}`} 
                            key={player.id}
                            className="group bg-[#1e1e1e] border border-white/5 rounded-2xl p-4 flex items-center gap-4 hover:border-white/20 hover:bg-[#252525] transition-all cursor-pointer"
                        >
                            <div className="relative shrink-0">
                                <img 
                                    src={`https://sg-public-api.royolands.com/rpweb/morda/${player.nickname}`} 
                                    alt={player.nickname} 
                                    className="w-14 h-14 rounded-lg"
                                />
                                {player.isOnline && (
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-[3px] border-[#1e1e1e] rounded-full"></div>
                                )}
                            </div>
                            <div className="overflow-hidden">
                                <h4 className="text-white font-bold truncate group-hover:text-indigo-400 transition">{player.nickname}</h4>
                                <div className="flex flex-col mt-1">
                                    <p className="text-xs font-medium text-indigo-300/80">
                                        {t.stats.playedHours.replace('{hours}', (player.totalMinutes / 60).toFixed(1))}
                                    </p>
                                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                                        {player.isOnline 
                                            ? <span className="text-green-500">{t.stats.inGameNow}</span> 
                                            : t.stats.lastSeen
                                                .replace('{date}', new Date(player.lastSeen).toLocaleDateString(dateLocale))
                                                .replace('{time}', new Date(player.lastSeen).toLocaleTimeString(dateLocale, {hour: '2-digit', minute:'2-digit'}))
                                        }
                                    </p>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {hasMore && (
                    <div className="flex justify-center">
                        <button 
                            onClick={loadMore}
                            disabled={isLoadingMore}
                            className="flex items-center gap-2 px-6 py-3 bg-[#1e1e1e] hover:bg-[#252525] border border-white/5 rounded-xl text-zinc-300 font-medium transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoadingMore ? <Loader2 size={18} className="animate-spin" /> : <ChevronDown size={18} />}
                            {isLoadingMore ? t.stats.loadingMore : t.stats.showMore}
                        </button>
                    </div>
                )}
            </>
        )}

      </div>
    </DashboardLayout>
  );
}